import { createSlice } from "@reduxjs/toolkit";
import { COUNTRY_INFO } from "../../constans/countries/countries";
import { auth } from "../../firebase.config";
import {
    loadVisitedlistFromFirestore,
    saveVisitedlistToFirestore,
    updateVisitedMetaInFirestore,
} from "../../constans/functions/visitedlistServices";
import {
    loadVisitedlistFromStorage,
    saveVisitedlistToStorage
} from "../../constans/functions/storage";
import { showVisitedlistNotification } from "../../constans/functions/notificationSlice";


export const VISITED_STATUS = {
    LOVED: 'loved',
    AGAIN: 'again',
    ONCE: 'once',
    HIDDEN_GEM: 'hidden_gem'
};

const normalizeVisitedData = (data) => {
    if (!data || data.length === 0) {
        return { 
            visited: [], 
            visitMeta: {} 
        };
    }
    if (typeof data[0] === 'number') {
        return { 
            visited: data, 
            visitMeta: {} 
        };
    }
    if (typeof data[0] === 'object' && data[0].countryId !== undefined) {
        const visited = data.map(item => item.countryId);
        const visitMeta = {};
        data.forEach(item => {
            visitMeta[item.countryId] = {
                status: item.status || null,
                visitedMonth: item.visitedMonth || null,
                visitedYear: item.visitedYear || null,
                note: item.note || ''
            }
        });
        return { visited, visitMeta };
    }
    return { visited: [], visitMeta: {} };
};

const updateCountryInfoVisited = (visited, visitMeta = {}) => {
    Object.keys(COUNTRY_INFO).forEach(id => {
        COUNTRY_INFO[id].visited = false;
        COUNTRY_INFO[id].visited_status = null;
    });
    visited.forEach(id => {
        if (COUNTRY_INFO[id]) {
            COUNTRY_INFO[id].visited = true;
            COUNTRY_INFO[id].visited_status = visitMeta[id]?.status || null;
        }
    });
};

export const visitedSlice = createSlice({
    name: 'visitedList',
    initialState: {
        visited: [],
        visitMeta: {},
        isLoading: false,
        isLoadedFromCache: false
    },
    reducers: {
        setVisitedlist: (state, { payload }) => {
            const { visited, visitMeta } = normalizeVisitedData(payload);
            state.visited = visited;
            state.visitMeta = visitMeta;
            state.isLoading = false;
            updateCountryInfoVisited(visited, visitMeta);
            saveVisitedlistToStorage({
                visited,
                visitMeta
            });
        },

        setVisitedlistFromCache: (state, { payload }) => {
            if (Array.isArray(payload)) {
                state.visited = payload;
                state.visitMeta = {};
            } else {
                state.visited = payload.visited || [];
                state.visitMeta = payload.visitMeta || {};
            }
            updateCountryInfoVisited(state.visited, state.visitMeta);
            state.isLoadedFromCache = true;
            state.isLoading = false;
        },

        addToVisitedList: (state, { payload }) => {
            if (!state.visited.includes(payload)) {
                state.visited.push(payload);
                state.visitMeta[payload] = {
                    status: null, 
                    visitedMonth: null,
                    visitedYear: null,
                    note: ''
                };
                if (COUNTRY_INFO[payload]) {
                    COUNTRY_INFO[payload].visited = true;
                    COUNTRY_INFO[payload].visited_status = null;
                }
                saveVisitedlistToStorage({
                    visited: state.visited,
                    visitMeta: state.visitMeta
                });
            }
        },

        removeFromVisitedList: (state, { payload }) => {
            state.visited = state.visited.filter(id => id !== payload);
            delete state.visitMeta[payload];
            if (COUNTRY_INFO[payload]) {
                COUNTRY_INFO[payload].visited = false;
                COUNTRY_INFO[payload].visited_status = null;
            }
            saveVisitedlistToStorage({
                visited: state.visited,
                visitMeta: state.visitMeta
            });
        },

        updateVisitedMeta: (state, { payload }) => {
            const { countryCode, updates } = payload;
            if (state.visited.includes(countryCode)) {
                if (updates === null) {
                    state.visitMeta[countryCode] = {
                        status: null,
                        visitedMonth: null,
                        visitedYear: null,
                        note: ''
                    };
                } else {
                    state.visitMeta[countryCode] = {
                        ...state.visitMeta[countryCode],
                        ...updates,
                        note:
                            updates.note === null
                                ? ''
                                : updates.note !== undefined
                                    ? updates.note.slice(0, 150)
                                    : state.visitMeta[countryCode]?.note || ''
                    };
                }
                if (COUNTRY_INFO[countryCode]) {
                    COUNTRY_INFO[countryCode].visited_status = 
                        state.visitMeta[countryCode]?.status || null;
                }
                saveVisitedlistToStorage({
                    visited: state.visited,
                    visitMeta: state.visitMeta
                });
            }
        },

        clearVisitedList: (state) => {
            state.visited.forEach(id => {
                if (COUNTRY_INFO[id]) {
                    COUNTRY_INFO[id].visited = false;
                    COUNTRY_INFO[id].visited_status = null;
                }
            });
            state.visited = [];
            state.visitMeta = {};
            saveVisitedlistToStorage({
                visited: [],
                visitMeta: {}
            });
        },
        setLoading: (state, { payload }) => {
            state.isLoading = payload;
        },
        setLoadedFromCache: (state, { payload }) => {
            state.isLoadedFromCache = payload;
        }
    }
});

export const {
    addToVisitedList,
    removeFromVisitedList,
    updateVisitedMeta,
    clearVisitedList,
    setVisitedlist,
    setVisitedlistFromCache,
    setLoading,
    setLoadedFromCache
} = visitedSlice.actions;

// Thunks
export const addCountryToVisitedWithSync = (countryCode) => async (dispatch, getState) => {
    try {
        dispatch(addToVisitedList(countryCode));
        if (auth.currentUser) {
            const { visited, visitMeta } = getState().visitedList;
            const payload = visited.map(id => ({
                countryId: id,
                ...visitMeta[id]
            }));
            await saveVisitedlistToFirestore(auth.currentUser.uid, payload);
        }
        dispatch(showVisitedlistNotification());
    } catch (err) {
        console.error('Error adding country to visited:', err);
        dispatch(removeFromVisitedList(countryCode));
    }
};

export const removeCountryFromVisitedWithSync = (countryCode) => async (dispatch, getState) => {
    try {
        dispatch(removeFromVisitedList(countryCode));
        if (auth.currentUser) {
            const { visited, visitMeta } = getState().visitedList;
            const payload = visited.map(id => ({
                countryId: id,
                ...visitMeta[id]
            }));
            await saveVisitedlistToFirestore(auth.currentUser.uid, payload);
        }
    } catch (err) {
        console.error('Error removing country from visited:', err);
        dispatch(addToVisitedList(countryCode));
    }
};

export const updateVisitedMetaSync = (countryCode, updates) => async (dispatch) => {
    try {
      dispatch(updateVisitedMeta({ countryCode, updates }));
      if (auth.currentUser) {
        await updateVisitedMetaInFirestore(
          auth.currentUser.uid,
          countryCode,
          updates
        );
      }
    } catch (err) {
      console.error('Error updating visited status:', err);
    }
  };

export const clearVisitedListWithSync = () => async (dispatch) => {
    try {
        dispatch(clearVisitedList());
        if (auth.currentUser) {
            await saveVisitedlistToFirestore(auth.currentUser.uid, []);
        }
    } catch (err) {
        console.error('Error clearing visited list:', err);
    }
};

export const loadUserVisitedlist = () => async (dispatch) => {
    if (!auth.currentUser) return;
    const cachedData = loadVisitedlistFromStorage();
    if (cachedData?.visited?.length > 0) {
        dispatch(setVisitedlistFromCache(cachedData));
    }
    try {
        dispatch(setLoading(true));
        const firestoreData = await loadVisitedlistFromFirestore(auth.currentUser.uid);
        dispatch(setVisitedlist(firestoreData))
        dispatch(setLoading(false))
    } catch (err) {
        console.error('Error loading visitedlist:', err);
        dispatch(setLoading(false));
    }
};